<script setup lang="ts">
    import { onMounted, ref, computed } from 'vue';
    import { useRouter } from 'vue-router';
    import { http } from '@/shared/httpWrapper';
    import { CorrelatedEventDetailsResponse, getCorrelatedEventDetails, genericViewInspectSql } from '@/shared/helpers/api';
    import { longDate } from '@/formatters';
    import { getClientApplicationDetailsLink, getLibraryLink, getSqlEditorLinkBySavedQuery } from '@/shared/helpers/routing';
    import { format as formatSql } from 'sql-formatter';

    const props = defineProps<{
        correlationId: string;
    }>();

    const router = useRouter();

    const details = ref<CorrelatedEventDetailsResponse | null>(null);
    const compiledSql = ref<string | null>(null);
    const loading = ref(false);

    const firstEvent = computed(() => details.value?.events?.[0] ?? null);

    const rawMessage = computed(() => details.value?.events?.find(e => e.messageValue)?.messageValue ?? null);

    const messageIsQuerySpec = computed(() => {
        const raw = rawMessage.value;
        if (!raw) return false;
        return raw.startsWith('{"Version":1');
    });

    async function load(): Promise<void> {
        try {
            loading.value = true;
            details.value = await getCorrelatedEventDetails(http, props.correlationId);
            await tryCompileSql();
        } finally {
            loading.value = false;
        }
    }

    async function tryCompileSql(): Promise<void> {
        if (messageIsQuerySpec.value) {
            try {
                const querySpec = JSON.parse(rawMessage.value!);
                const result = await genericViewInspectSql(http, querySpec);
                compiledSql.value = formatSql(result.data.sql, { language: 'tsql' });
            } catch {
                // compilation failed — ignore
            }
        }
    }

    onMounted(load);

    const resources = computed(() => {
        const unique = new Map<string, { name: string; type: number }>();
        for (const e of details.value?.events ?? []) {
            if (e.resourceName) {
                unique.set(e.resourceName, { name: e.resourceName, type: e.resourceType });
            }
        }
        return [...unique.values()];
    });

    const message = computed(() => {
        if (details.value?.savedQuery) return null;
        if (messageIsQuerySpec.value) {
            try {
                return JSON.stringify(JSON.parse(rawMessage.value!), null, 2);
            } catch {
                // fall through
            }
        }
        return rawMessage.value;
    });
</script>

<template>
    <div>
        <h1 class="title mb-1">
            Correlated event details
        </h1>
        <p class="subtitle mb-1">
            Correlation ID: {{ props.correlationId }}
        </p>
        <p>
            <a
                href="#"
                @click.prevent="router.back()">
                &laquo; Back
            </a>
        </p>
        <hr>
        <b-loading
            v-model="loading"
            :is-full-page="false"
            :can-cancel="false" />

        <div v-if="details">
            <div class="columns">
                <div class="column is-one-third">
                    <div
                        v-if="details.application"
                        class="block">
                        <h2 class="title is-5">
                            Client application
                        </h2>
                        <table class="table is-narrow">
                            <tbody>
                                <tr>
                                    <th>Name</th>
                                    <td>
                                        <router-link
                                            v-if="details.application.name"
                                            :to="getClientApplicationDetailsLink(details.application.name)">
                                            {{ details.application.description || details.application.name }}
                                        </router-link>
                                        <span v-else>—</span>
                                    </td>
                                </tr>
                                <tr>
                                    <th>Identity</th>
                                    <td>{{ details.application.identity }}</td>
                                </tr>
                                <tr>
                                    <th>Application ID</th>
                                    <td>{{ details.application.applicationId }}</td>
                                </tr>
                                <tr>
                                    <th>Object ID</th>
                                    <td>{{ details.application.objectId }}</td>
                                </tr>
                                <tr v-if="details.application.homepage">
                                    <th>Homepage</th>
                                    <td>
                                        <a
                                            :href="details.application.homepage"
                                            target="_blank"
                                            rel="noopener noreferrer">
                                            {{ details.application.homepage }}
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div
                        v-else
                        class="block">
                        <b-message type="is-warning">
                            No client application information found for this correlation.
                        </b-message>
                    </div>

                    <div
                        v-if="firstEvent"
                        class="block">
                        <h2 class="title is-5">
                            Request summary
                        </h2>
                        <table class="table is-narrow">
                            <tbody>
                                <tr>
                                    <th>Timestamp</th>
                                    <td>{{ longDate(firstEvent.timestamp) }}</td>
                                </tr>
                                <tr>
                                    <th>Endpoint</th>
                                    <td>{{ firstEvent.endpoint }}</td>
                                </tr>
                                <tr v-if="firstEvent.scopeArgument">
                                    <th>Scope</th>
                                    <td>{{ firstEvent.scopeArgument }}</td>
                                </tr>
                                <tr>
                                    <th>Success</th>
                                    <td>
                                        <b-icon
                                            :icon="firstEvent.success ? 'check' : 'close'"
                                            :type="firstEvent.success ? 'is-success' : 'is-danger'" />
                                    </td>
                                </tr>
                                <tr>
                                    <th>Duration (ms)</th>
                                    <td>{{ firstEvent.durationMilliseconds }}</td>
                                </tr>
                                <tr>
                                    <th>Result count</th>
                                    <td>{{ firstEvent.objectCount }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        v-if="resources.length"
                        class="block">
                        <h2 class="title is-5">
                            Resources ({{ resources.length }})
                        </h2>
                        <div class="tags">
                            <template
                                v-for="r in resources"
                                :key="r.name">
                                <router-link
                                    v-if="r.type === 1 && !r.name.startsWith('_')"
                                    :to="getLibraryLink(r.name)"
                                    class="tag is-medium is-link is-light">
                                    {{ r.name }}
                                </router-link>
                                <span
                                    v-else
                                    class="tag is-medium">
                                    {{ r.name }}
                                </span>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="column">
                    <div
                        v-if="details.savedQuery"
                        class="block">
                        <h2 class="title is-5">
                            Saved query: {{ details.savedQuery.name }}
                        </h2>
                        <p class="block">
                            This is the query as it is currently saved, not necessarily how it looked when the request was made.
                        </p>
                        <pre class="is-size-6">{{ details.savedQuery.spec }}</pre>
                        <div class="mt-3">
                            <router-link
                                :to="getSqlEditorLinkBySavedQuery(details.savedQuery.guid)"
                                class="button">
                                Open in SQL editor
                            </router-link>
                        </div>
                    </div>
                    <div
                        v-else-if="message"
                        class="block">
                        <h2 class="title is-5">
                            Request / query / error
                        </h2>
                        <pre class="is-size-6">{{ message }}</pre>
                    </div>
                    <div
                        v-if="compiledSql"
                        class="block">
                        <h2 class="title is-5">
                            Compiled SQL
                        </h2>
                        <pre class="is-size-6">{{ compiledSql }}</pre>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
pre {
    white-space: pre-wrap;
    overflow-wrap: break-word;
}
</style>
